import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Gift, Clock, Sparkles, HelpCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCountdown = (secs) => {
  if (secs <= 0) return null;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

/**
 * Load-to-get bonus promo. Shows the reward tiers + a live countdown to the
 * player's next available bonus. `compact` renders a slim one-line strip for
 * placement on the lobby; the full version is used on the Buy Credits screen.
 */
const LoadBonusPromo = ({ compact = false, onCtaClick, context = 'load' }) => {
  const [info, setInfo] = useState(null);
  const [secs, setSecs] = useState(0);
  const [showHow, setShowHow] = useState(false);
  const tick = useRef(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/rewards/load-bonus-info`)
      .then(({ data }) => {
        if (!alive) return;
        setInfo(data);
        setSecs(data?.seconds_remaining || 0);
      })
      .catch(() => { /* silently hide */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (secs <= 0) return;
    tick.current = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(tick.current);
  }, [secs > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!info || !info.enabled) return null;

  const countdown = fmtCountdown(secs);
  const readyNow = secs <= 0;
  const mult = Math.round(info.wagering_multiplier || 0);
  const first = (info.tiers || [])[0];
  const offer = first ? `$${Math.round(first.min)}+ for ${String(first.reward).replace(/^\+/, '')}` : '';
  const verb = context === 'playback' ? 'Play back' : 'Load or play back';

  if (compact) {
    return (
      <div
        data-testid="load-bonus-promo-compact"
        onClick={onCtaClick}
        role={onCtaClick ? 'button' : undefined}
        style={{
          cursor: onCtaClick ? 'pointer' : 'default',
          borderRadius: 14, padding: '12px 16px', margin: '10px 0',
          background: 'linear-gradient(100deg,#0d2b12,#124a1f 55%,#1c6b2c)',
          border: '1.5px solid #39FF14aa', color: '#eafff0',
          display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 6px 22px rgba(57,255,20,0.18)',
        }}
      >
        <Gift size={22} style={{ color: '#39FF14', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 900, fontSize: 14, letterSpacing: '0.01em' }}>
            {context === 'playback' ? 'Play back & get Free Play — up to 20% back' : <>Load &amp; get Free Play — up to 20% back</>}
          </div>
          <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 700 }} data-testid="load-bonus-compact-status">
            {readyNow
              ? `Bonus ready: ${verb.toLowerCase()} ${offer}`
              : `Next bonus unlocks in ${countdown} · then ${verb.toLowerCase()} ${offer}`}
          </div>
        </div>
        <span style={{
          fontSize: 12, fontWeight: 900, padding: '5px 12px', borderRadius: 999,
          background: readyNow ? '#39FF14' : 'rgba(255,255,255,0.12)',
          color: readyNow ? '#052500' : '#eafff0', flexShrink: 0,
        }}>
          {readyNow ? 'READY' : countdown}
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="load-bonus-promo"
      style={{
        marginTop: 20, borderRadius: 16, padding: '18px 22px', position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(100deg,#0d2b12,#124a1f 55%,#1c6b2c)',
        border: '2px solid #39FF14aa', color: '#eafff0',
        boxShadow: '0 10px 34px rgba(57,255,20,0.30)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Gift size={34} strokeWidth={2.4} style={{ color: '#39FF14' }} />
        <div>
          <div style={{ fontSize: 22, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.01em' }}>
            Load or Play Back &amp; Get Free Play 🎁
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2, opacity: 0.9 }}>
            The more you load or play back, the bigger the bonus. Available once every {Math.round(info.interval_hours)} hours.
          </div>
        </div>
      </div>

      {/* Tier rows */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
        {(info.tiers || []).map((t, i) => (
          <div key={i} data-testid={`load-bonus-tier-${i}`} style={{
            flex: '1 1 150px', minWidth: 140, background: 'rgba(0,0,0,0.28)',
            border: '1px solid #39FF1455', borderRadius: 12, padding: '10px 12px',
          }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#bfffcf' }}>{t.label}</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#39FF14', marginTop: 2 }}>{t.reward}</div>
          </div>
        ))}
      </div>

      {/* Countdown + wagering note */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        <div data-testid="load-bonus-countdown" style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 14px', borderRadius: 999, fontWeight: 900, fontSize: 13,
          background: readyNow ? '#39FF14' : 'rgba(0,0,0,0.32)',
          color: readyNow ? '#052500' : '#eafff0',
          border: readyNow ? 'none' : '1px solid #39FF1455',
        }}>
          <Clock size={15} />
          {readyNow ? `Bonus ready: load or play back ${offer}` : `Next bonus unlocks in ${countdown} · then load or play back ${offer}`}
        </div>
        <span style={{ fontSize: 12, opacity: 0.8, fontWeight: 700 }}>
          Free Play · play it through {mult}× to cash out
        </span>
      </div>

      {/* Plain-English 3× explainer — answers the #1 player question */}
      <button
        data-testid="load-bonus-how-toggle"
        onClick={() => setShowHow((v) => !v)}
        style={{
          marginTop: 12, background: 'transparent', border: 'none', cursor: 'pointer',
          color: '#bfffcf', fontWeight: 800, fontSize: 13, display: 'inline-flex',
          alignItems: 'center', gap: 6, padding: 0,
        }}
      >
        <HelpCircle size={15} /> What does &ldquo;{mult}× to cash out&rdquo; mean?
      </button>
      {showHow && (
        <div data-testid="load-bonus-how-text" style={{
          marginTop: 8, background: 'rgba(0,0,0,0.28)', border: '1px solid #39FF1433',
          borderRadius: 12, padding: '12px 14px', fontSize: 13, lineHeight: 1.6, color: '#eafff0',
        }}>
          <b>Free Play is yours to play with right away.</b> Say you earn <b>$10 Free Play</b> —
          just play it in any game. Once you&rsquo;ve wagered <b>{mult}× that amount
          (${(10 * mult).toFixed(0)} of total play on a $10 bonus)</b>, your Free Play <i>and everything
          you won with it</i> turns into real cash you can withdraw.
          <div style={{ marginTop: 8, opacity: 0.9 }}>
            💡 Your own deposit is always yours — you only play through the <b>bonus</b>, never your
            loaded cash. You just can&rsquo;t cash out until the {mult}× is done.
          </div>
        </div>
      )}
      <Sparkles size={120} style={{ position: 'absolute', right: -18, top: -18, opacity: 0.12, color: '#39FF14' }} />
    </div>
  );
};

export default LoadBonusPromo;
